<template>
  <Dialog
    v-bind="filteredAttrs"
    v-model="isOpen"
    :content-class="'order-share-dialog'"
    :persistent="dataLoading"
  >
    <template
      v-if="dataLoading"
      #content
    >
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
        />
      </div>
    </template>
    <template
      v-else
      #content
    >
      <div class="sub-title mb-5">
        {{ $t("Order is shared with:") }}
      </div>
      <div v-show="orderUsers.length > 0">
        <v-data-table
          :headers="headers"
          :items="orderUsers"
          :loading="dataLoading"
          :loading-text="$t('Loading Users')"
          :no-data-text="t('No data available')"
          hide-default-footer
          :items-per-page="-1"
        >
          <template #item.controls="slotProps">
            <Button
              color="ma-1 text-primary"
              :icon-btn="true"
              :icon-with-background="true"
              small
              @click="deleteOrderUser(slotProps.item)"
            >
              <v-icon
                class="icon-flipped"
                size="small"
              >
                mdi-delete
              </v-icon>
            </Button>
          </template>
        </v-data-table>
      </div>
      <div v-show="orderUsers.length <= 0">
        <div class="order-share-dialog__users-not-found">
          {{ $t("No shared users found.") }}
        </div>
      </div>
      <Button
        class="share-btn mt-6"
        @click="showMoreUserOrderShareDialog = true"
      >
        {{ $t("Share with additional healthcare professionals") }}
      </Button>
      <MoreUserOrderShareDialog
        v-model="showMoreUserOrderShareDialog"
        max-width="1000px"
        :title="
          t('Share Order - Order {orderId}', {
            orderId: orderId,
          })
        "
        :order-id="orderId"
        :order-users="orderUsers"
        @cancel="showMoreUserOrderShareDialog = false"
        @closeSubmitOrderShareDialog="closeSubmitOrderShareDialog"
      />
    </template>
    <template #actions>
&nbsp;
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, useAttrs } from 'vue';
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n';
import Dialog from "@/components/dialogs/Dialog.vue";
import { OrderUser } from "@/entities/order-user";
import Button from "@/components/input-components/Button.vue";
import MoreUserOrderShareDialog from '@/components/dialogs/MoreUserOrderShareDialog.vue'

defineOptions({ name: "OrderShareDialog", inheritAttrs: false });

const { t } = useI18n();
const attrs = useAttrs();

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  dataLoading: {
    type: Boolean,
    required: true,
    default: () => false,
  },
  order: {
    type: Object as PropType<{ orderId: number }>,
    required: true,
    default: () => ({ orderId: -1 }),
  },
  orderShareholders: {
    type: Array as PropType<Array<OrderUser>>,
    required: true,
    default: () => [],
  },
  orderUsers: {
    type: Array as PropType<OrderUser[]>,
    required: false,
    default: () => [],
  },
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  deleteEmail: [{ data: OrderUser }]
  save: []
  cancel: []
  closeSubmitOrderShareDialog: [string | null?]
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit("update:modelValue", val),
});

const filteredAttrs = computed(() => {
  const { modelValue, ...rest } = attrs;
  return rest;
});

const headers = ref([
  { title: t("Salutation") as string, value: "doctorSalutation" },
  { title: t("Title") as string, value: "doctorTitle" },
  { title: t("First name") as string, value: "doctorFirstName" },
  { title: t("Last name") as string, value: "doctorLastName" },
  { title: t("Email") as string, value: "doctorEmail" },
  { title: "", value: "controls", sortable: false },
]);

const showMoreUserOrderShareDialog = ref(false);

const orderUsers = computed<Array<OrderUser>>(() =>
  props.orderShareholders.filter((u) => !u.isOwner)
);

const orderId = computed<number>(() => props.order.orderId);

async function deleteOrderUser(data: OrderUser) {
  emit('deleteEmail', { data });
}

function closeSubmitOrderShareDialog(args: string | null) {
  showMoreUserOrderShareDialog.value = false;
  emit('closeSubmitOrderShareDialog', args ?? undefined);
  if (args === 'finish') {
    emit('save');
  }
}
</script>

<style lang="scss">
.order-share-dialog {
  &__intro-text {
    text-transform: uppercase;
    font-size: 1.25rem;
    color: black;
    font-weight: 800;
    margin-bottom: 1rem;
    letter-spacing: 0.05rem;
  }

  .v-card > .v-card__text {
    padding-bottom: 1rem;
  }

  .button-add {
    flex: 25% 0;
  }

  .input-email-add {
    flex: 75% 1;
  }

  .v-card__actions {
    display: none;
  }

  .sub-title {
    font-weight: bold;
  }

  .share-btn {
    display: block;
    white-space: break-spaces;
  }
}
</style>
